import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  MessageSquare, Plus, Inbox, Send, X, Loader2, AlertCircle, Clock3,
  HelpCircle, MessageCircleQuestion, Lightbulb, User as UserIcon, CheckCircle2,
  ArrowUpRight, ArrowDownLeft, MessagesSquare,
  Paperclip, Download, FileText, FileSpreadsheet, FileImage, File as FileIcon,
} from "lucide-react";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { FilePreviewModal } from "../components/common/FilePreviewModal";
import { SearchBar, useDebouncedValue, Highlight } from "../components/common/SearchBar";
import { UserAvatar } from "../components/UserAvatar";
import { UserCardDialog } from "../components/UserCardDialog";

// ───────────────────────────────────────────────────────────────────
// Costanti & helpers
// ───────────────────────────────────────────────────────────────────

const CATEGORY = {
  richiesta:    { label: "Richiesta",    icon: MessageCircleQuestion, color: "text-blue-600 bg-blue-50 border-blue-200" },
  informazione: { label: "Informazione", icon: Lightbulb,             color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  domanda:      { label: "Domanda",      icon: HelpCircle,            color: "text-amber-600 bg-amber-50 border-amber-200" },
};

const STATUS = {
  aperto:         { label: "Aperto",         className: "wl-badge wl-badge--warning" },
  in_lavorazione: { label: "In lavorazione", className: "wl-badge wl-badge--info" },
  risolto:        { label: "Risolto",        className: "wl-badge wl-badge--success" },
};

// ─── Attachments ────────────────────────────────────────────────────
const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024; // 15 MB (limite fine per tipo lato server)
const MAX_ATTACHMENTS_PER_MESSAGE = 5;
const ALLOWED_ATTACHMENT_EXTS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".csv", ".png", ".jpg", ".jpeg"];
const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTS.join(",");
const ALLOWED_ATTACHMENT_MIMES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/jpg",
]);

const fmtFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const pickAttachmentIcon = (mimeOrName = "") => {
  const s = String(mimeOrName).toLowerCase();
  if (s.includes("image") || s.endsWith(".png") || s.endsWith(".jpg") || s.endsWith(".jpeg")) return FileImage;
  if (s.includes("pdf") || s.endsWith(".pdf")) return FileText;
  if (s.includes("word") || s.endsWith(".doc") || s.endsWith(".docx")) return FileText;
  if (s.includes("excel") || s.includes("sheet") || s.endsWith(".xls") || s.endsWith(".xlsx")) return FileSpreadsheet;
  return FileIcon;
};

const validateAttachmentFile = (file) => {
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  const mime = (file.type || "").toLowerCase();
  const extOk = ALLOWED_ATTACHMENT_EXTS.includes(ext);
  const mimeOk = ALLOWED_ATTACHMENT_MIMES.has(mime);
  if (!extOk && !mimeOk) {
    return "Formato non supportato. Puoi caricare PDF, Word, Excel, CSV e immagini.";
  }
  if (file.size === 0) return "File vuoto.";
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `File troppo grande (max ${MAX_ATTACHMENT_SIZE / (1024 * 1024)} MB).`;
  }
  return null;
};

const uploadInternalAttachments = async (ticketId, messageId, files) => {
  let ok = 0;
  for (const f of files) {
    const fd = new FormData();
    fd.append("ticket_id", ticketId);
    fd.append("message_id", messageId);
    fd.append("file", f);
    try {
      await api.post("/internal/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      ok += 1;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Errore caricamento "${f.name}".`);
    }
  }
  return ok;
};

const downloadInternalAttachment = async (fileId, filename) => {
  try {
    const res = await api.get(`/internal/attachments/${fileId}`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `allegato_${fileId.slice(0, 8)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Errore download allegato.");
  }
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const fmtRel = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} g fa`;
  return fmtDateTime(iso);
};


// ───────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, className: "wl-badge wl-badge--neutral" };
  return (
    <span
      data-testid={`im-status-${status}`}
      className={s.className}
    >
      {s.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const c = CATEGORY[category];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${c.color}`}>
      <Icon size={11} strokeWidth={2} />
      {c.label}
    </span>
  );
};

const Section = ({ title, icon: Icon, children, action }) => (
  <div className="bg-surface border border-border rounded-lg p-5">
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} strokeWidth={2} className="text-accent" />}
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);


// ───────────────────────────────────────────────────────────────────
// Attachments UI (identica al Supporto ma isolata per DRY→isolamento modulo)
// ───────────────────────────────────────────────────────────────────

const AttachmentPicker = ({ files, setFiles, disabled = false, testIdPrefix = "im-att" }) => {
  const inputRef = useRef(null);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!picked.length) return;

    const nextList = [...files];
    for (const f of picked) {
      if (nextList.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
        toast.error(`Massimo ${MAX_ATTACHMENTS_PER_MESSAGE} allegati per messaggio.`);
        break;
      }
      const err = validateAttachmentFile(f);
      if (err) { toast.error(`"${f.name}": ${err}`); continue; }
      if (nextList.some((x) => x.name === f.name && x.size === f.size)) continue;
      nextList.push(f);
    }
    setFiles(nextList);
  };

  const removeAt = (idx) => setFiles(files.filter((_, i) => i !== idx));
  const remaining = MAX_ATTACHMENTS_PER_MESSAGE - files.length;

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || remaining <= 0}
          data-testid={`${testIdPrefix}-pick-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-subtle hover:border-accent text-xs font-medium text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Paperclip size={13} strokeWidth={2} />
          Allega file
        </button>
        <span className="text-[11px] text-text-muted">
          {files.length}/{MAX_ATTACHMENTS_PER_MESSAGE} · PDF, Word, Excel, PNG, JPG · max 25 MB (immagini 10 MB)
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={onPick}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-1" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, idx) => {
            const Icon = pickAttachmentIcon(f.type || f.name);
            return (
              <li
                key={`${f.name}-${f.size}-${idx}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-border bg-subtle text-xs"
                data-testid={`${testIdPrefix}-item-${idx}`}
              >
                <Icon size={13} className="text-accent flex-shrink-0" />
                <span className="flex-1 truncate text-text-primary">{f.name}</span>
                <span className="text-text-muted whitespace-nowrap">{fmtFileSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  disabled={disabled}
                  className="text-text-muted hover:text-red-500 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                  title="Rimuovi"
                >
                  <X size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const AttachmentList = ({ attachments = [] }) => {
  const [preview, setPreview] = useState(null);
  if (!attachments.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="im-msg-attachments">
      {attachments.map((a) => {
        const Icon = pickAttachmentIcon(a.mime || a.filename);
        return (
          <button
            key={a.file_id}
            type="button"
            onClick={() => setPreview(a)}
            data-testid={`im-attachment-${a.file_id}`}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-surface hover:border-accent hover:bg-accent/5 text-[11px] text-text-primary transition-colors max-w-full"
            title={`Visualizza ${a.filename}`}
          >
            <Icon size={12} className="text-accent flex-shrink-0" />
            <span className="truncate max-w-[180px]">{a.filename}</span>
            <span className="text-text-muted whitespace-nowrap">{fmtFileSize(a.size)}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); downloadInternalAttachment(a.file_id, a.filename); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); downloadInternalAttachment(a.file_id, a.filename); } }}
              data-testid={`im-attachment-dl-${a.file_id}`}
              title={`Scarica ${a.filename}`}
              className="p-0.5 rounded hover:bg-accent/10"
            >
              <Download size={11} className="text-text-muted flex-shrink-0" />
            </span>
          </button>
        );
      })}
      {preview && (
        <FilePreviewModal
          open
          onClose={() => setPreview(null)}
          filename={preview.filename}
          fetchPath={`/internal/attachments/${preview.file_id}?inline=1`}
          onDownload={() => downloadInternalAttachment(preview.file_id, preview.filename)}
        />
      )}
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Modal: nuovo ticket interno
// ───────────────────────────────────────────────────────────────────

const NewInternalTicketModal = ({ open, onClose, onCreated }) => {
  const [recipients, setRecipients] = useState([]);
  const [recipientIds, setRecipientIds] = useState([]);  // multi-select
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("richiesta");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubject(""); setCategory("richiesta"); setDescription(""); setRecipientIds([]); setFiles([]);
      return;
    }
    setLoadingRecipients(true);
    api.get("/internal/recipients")
      .then(({ data }) => setRecipients(data || []))
      .catch(() => toast.error("Impossibile caricare i colleghi dell'azienda."))
      .finally(() => setLoadingRecipients(false));
  }, [open]);

  if (!open) return null;

  const toggleRecipient = (id) => {
    setRecipientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    if (recipientIds.length === recipients.length) setRecipientIds([]);
    else setRecipientIds(recipients.map((u) => u.id));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (recipientIds.length === 0) return toast.error("Seleziona almeno un destinatario.");
    if (subject.trim().length < 3) return toast.error("L'oggetto deve avere almeno 3 caratteri.");
    if (description.trim().length < 5) return toast.error("Il messaggio deve avere almeno 5 caratteri.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/internal/tickets", {
        recipient_user_ids: recipientIds,
        subject: subject.trim(),
        category,
        description: description.trim(),
      });
      // Upload allegati sul primo messaggio se presenti
      if (files.length && data?.first_message_id) {
        const ok = await uploadInternalAttachments(data.id, data.first_message_id, files);
        if (ok > 0) {
          toast.success(
            ok === files.length
              ? (recipientIds.length === 1 ? "Messaggio inviato con allegati." : `Messaggio inviato a ${recipientIds.length} colleghi con allegati.`)
              : `Messaggio inviato. ${ok}/${files.length} allegati caricati.`
          );
        } else {
          toast.success("Messaggio inviato. Nessun allegato caricato.");
        }
      } else {
        toast.success(
          recipientIds.length === 1 ? "Messaggio inviato." : `Messaggio inviato a ${recipientIds.length} colleghi.`
        );
      }
      onCreated && onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile inviare il messaggio.");
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = recipients.length > 0 && recipientIds.length === recipients.length;

  return (
    <div className="wl-modal-overlay" data-testid="new-internal-modal">
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" aria-labelledby="new-im-title">
        <form onSubmit={submit} className="flex flex-col min-h-0">
          <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-accent" />
              <h3 id="new-im-title" className="wl-modal__title">Nuovo messaggio interno</h3>
            </div>
            <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-new-internal" aria-label="Chiudi">
              <X size={16} />
            </button>
          </div>

          <div className="px-6 pb-2 space-y-4 overflow-y-auto">
            <div className="wl-field">
              <div className="flex items-center justify-between">
                <span className="wl-label wl-label--required">
                  A chi è rivolto {recipientIds.length > 0 && (
                    <span className="text-text-muted font-normal">· {recipientIds.length} selezionat{recipientIds.length === 1 ? "o" : "i"}</span>
                  )}
                </span>
                {recipients.length > 1 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    data-testid="recipient-toggle-all"
                    className="text-[11px] text-accent hover:underline"
                  >
                    {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                )}
              </div>
              {loadingRecipients ? (
                <div className="text-xs text-text-muted py-2"><Loader2 size={12} className="inline animate-spin mr-1" />Carico colleghi…</div>
              ) : recipients.length === 0 ? (
                <div className="text-xs text-text-muted bg-surface border border-border rounded-md px-3 py-2">
                  Nessun collega nella tua azienda a cui scrivere.
                </div>
              ) : (
                <div
                  className="border border-border rounded-md bg-surface max-h-48 overflow-y-auto divide-y divide-border"
                  data-testid="recipient-list"
                >
                  {recipients.map((u) => {
                    const display = u.full_name || u.name || u.email.split("@")[0];
                    const checked = recipientIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-page transition-colors ${checked ? "bg-page" : ""}`}
                        data-testid={`recipient-item-${u.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRecipient(u.id)}
                          className="rounded border-border text-accent focus:ring-accent focus:ring-offset-0"
                          data-testid={`recipient-check-${u.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-text-primary truncate">
                            {display}
                            {u.role === "admin" && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-accent">admin</span>
                            )}
                          </div>
                          <div className="text-[11px] text-text-muted truncate">{u.email}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {/* Chips dei selezionati per feedback rapido */}
              {recipientIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1" data-testid="recipient-chips">
                  {recipientIds.map((rid) => {
                    const u = recipients.find((x) => x.id === rid);
                    const name = u ? (u.full_name || u.name || u.email.split("@")[0]) : "—";
                    return (
                      <span
                        key={rid}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[11px] font-medium border border-accent/30"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => toggleRecipient(rid)}
                          className="hover:text-red-500"
                          title="Rimuovi"
                        ><X size={10} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="wl-field">
              <span className="wl-label wl-label--required">Categoria</span>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(CATEGORY).map(([key, c]) => {
                  const Icon = c.icon;
                  const active = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCategory(key)}
                      data-testid={`im-category-${key}`}
                      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                        active ? `${c.color} border-current` : "bg-surface border-border text-text-secondary hover:border-accent"
                      }`}
                    >
                      <Icon size={13} strokeWidth={2} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="wl-field">
              <label htmlFor="im-subject" className="wl-label wl-label--required">Oggetto</label>
              <input
                id="im-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="es. Bolletta da rivedere per cliente Rossi"
                maxLength={120}
                data-testid="im-subject"
                className="wl-input"
              />
              <span className="wl-helper wl-helper--muted">Sintetizza il tema in una frase.</span>
            </div>

            <div className="wl-field">
              <label htmlFor="im-description" className="wl-label wl-label--required">Messaggio</label>
              <textarea
                id="im-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="es. Ciao, potresti verificare la fattura n. 2026/123?"
                rows={5}
                maxLength={4000}
                data-testid="im-description"
                className="wl-textarea"
              />
              <span className="wl-helper wl-helper--muted">{description.length}/4000 caratteri</span>
            </div>

            <div className="wl-field">
              <span className="wl-label">Allegati</span>
              <AttachmentPicker
                files={files}
                setFiles={setFiles}
                disabled={submitting}
                testIdPrefix="new-im-att"
              />
              <span className="wl-helper">Opzionale — PDF, Word, Excel, CSV, immagini · max 25 MB (immagini 10 MB) · fino a 5 file.</span>
            </div>
          </div>

          <div className="wl-modal__actions">
            <button type="button" onClick={onClose} disabled={submitting} className="wl-btn wl-btn--ghost" data-testid="cancel-new-internal">
              Annulla
            </button>
            <button type="submit" disabled={submitting || !recipients.length} className="wl-btn wl-btn--primary" data-testid="submit-new-internal">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Invia messaggio
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Thread del ticket
// ───────────────────────────────────────────────────────────────────

const InternalThread = ({ ticket, onClose, onChanged, currentUser }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [cardUserId, setCardUserId] = useState(null);

  const load = useCallback(async () => {
    if (!ticket) return;
    try {
      const { data } = await api.get(`/internal/tickets/${ticket.id}`);
      setDetail(data);
      // Il server ha appena marcato il thread come letto: aggiorna subito i badge
      window.dispatchEvent(new Event("wl-unread-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare il ticket.");
    } finally {
      setLoading(false);
    }
  }, [ticket]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e?.preventDefault();
    if (!reply.trim() && replyFiles.length === 0) return;
    if (!reply.trim()) {
      return toast.error("Aggiungi un testo al messaggio (anche breve).");
    }
    setSending(true);
    try {
      const { data: msg } = await api.post(`/internal/tickets/${ticket.id}/messages`, { body: reply.trim() });
      if (replyFiles.length && msg?.id) {
        const ok = await uploadInternalAttachments(ticket.id, msg.id, replyFiles);
        if (ok > 0 && ok < replyFiles.length) {
          toast.warning(`Solo ${ok}/${replyFiles.length} allegati caricati.`);
        }
      }
      setReply("");
      setReplyFiles([]);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio messaggio.");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.patch(`/internal/tickets/${ticket.id}/status`, { status: newStatus });
      toast.success(`Stato aggiornato: ${STATUS[newStatus].label}`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore cambio stato.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!ticket) return null;

  const status = detail?.status || ticket.status;
  const isResolved = status === "risolto";
  const t = detail || ticket;
  const recipientIds = (t.recipient_user_ids && t.recipient_user_ids.length)
    ? t.recipient_user_ids
    : (t.recipient_user_id ? [t.recipient_user_id] : []);
  const recipientNames = (t.recipient_user_names && t.recipient_user_names.length)
    ? t.recipient_user_names
    : (t.recipient_user_name ? [t.recipient_user_name] : []);
  const iAmOpener = t.opener_user_id === currentUser?.id;
  const iAmRecipient = recipientIds.includes(currentUser?.id);
  const recipientLabel = recipientNames.length === 0
    ? "—"
    : recipientNames.length === 1
      ? recipientNames[0]
      : `${recipientNames[0]} +${recipientNames.length - 1} altri`;
  const otherName = iAmOpener
    ? (recipientNames.length === 1 ? recipientNames[0] : `${recipientNames.length} colleghi`)
    : t.opener_user_name;

  return (
    <div className="wl-modal-overlay" data-testid="internal-thread-modal">
      <div className="wl-modal wl-modal--lg" role="dialog" aria-modal="true" aria-labelledby="im-thread-title" style={{maxHeight: '90vh'}}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CategoryBadge category={t.category} />
              <StatusBadge status={status} />
              <span
                className="inline-flex items-center gap-1 text-[11px] text-text-muted"
                title={recipientNames.join(", ")}
              >
                {iAmOpener
                  ? <><ArrowUpRight size={11} /> a {recipientLabel}</>
                  : <><ArrowDownLeft size={11} /> da {t.opener_user_name}</>}
              </span>
              {recipientNames.length > 1 && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent/10 text-accent text-[10px] font-medium border border-accent/30"
                  title={recipientNames.join(", ")}
                  data-testid="im-thread-multi-badge"
                >
                  Gruppo · {recipientNames.length}
                </span>
              )}
            </div>
            <h3 id="im-thread-title" className="text-sm font-semibold text-text-primary truncate" data-testid="im-thread-subject">
              {t.subject}
            </h3>
            <div className="text-[11px] text-text-muted mt-0.5">
              {fmtDateTime(t.created_at)}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-im-thread" aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        {/* Status controls (opener + recipient possono cambiare stato) */}
        {(iAmOpener || iAmRecipient) && (
          <div className="px-5 py-3 border-b border-border bg-subtle/40">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-text-muted font-medium">Stato:</span>
              {Object.entries(STATUS).map(([key, s]) => {
                const active = status === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => !active && !updatingStatus && changeStatus(key)}
                    disabled={active || updatingStatus}
                    data-testid={`im-set-status-${key}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                      active ? `${s.className} cursor-default` : "bg-surface border-border text-text-secondary hover:border-accent"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="im-thread-messages">
          {loading ? (
            <div className="text-sm text-text-muted text-center py-8">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Caricamento…
            </div>
          ) : (detail?.messages || []).length === 0 ? (
            <div className="text-sm text-text-muted text-center py-8">Nessun messaggio.</div>
          ) : (
            (detail.messages).map((m) => {
              const isSelf = m.author_user_id === currentUser?.id;
              const align = isSelf ? "items-end" : "items-start";
              const bubble = isSelf
                ? "bg-accent/10 border-accent/30 text-text-primary"
                : "bg-subtle border-border text-text-primary";
              return (
                <div key={m.id} className={`flex flex-col ${align}`} data-testid={`im-message-${m.id}`}>
                  <div className={`max-w-[85%] border rounded-lg px-3 py-2 ${bubble}`}>
                    <div className="text-[11px] font-medium mb-1 flex items-center gap-1.5">
                      {isSelf ? (
                        <span className="flex items-center gap-1.5">
                          <UserAvatar userId={m.author_user_id} name={m.author_user_name} size={18} />
                          <span>{m.author_user_name}</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCardUserId(m.author_user_id)}
                          className="flex items-center gap-1.5 hover:underline"
                          data-testid={`im-author-${m.id}`}
                          title={`Scheda di ${m.author_user_name}`}
                        >
                          <UserAvatar userId={m.author_user_id} name={m.author_user_name} size={18} />
                          <span>{m.author_user_name}</span>
                        </button>
                      )}
                      <span className="text-text-muted font-normal">· {fmtRel(m.created_at)}</span>
                    </div>
                    {m.body && (
                      <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                    )}
                    <AttachmentList attachments={m.attachments || []} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Reply */}
        {isResolved ? (
          <div className="px-5 py-4 border-t border-border bg-subtle/40 text-center text-xs text-text-muted">
            <CheckCircle2 size={14} className="inline mr-1 text-green-600" />
            Ticket risolto. Apri un nuovo messaggio se serve riaprire l&apos;argomento.
          </div>
        ) : (
          <form onSubmit={send} className="px-5 py-4 border-t border-border space-y-2">
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`es. Ciao ${otherName}, ho visto il tuo messaggio…`}
                rows={2}
                maxLength={4000}
                data-testid="im-reply-input"
                className="wl-textarea flex-1"
              />
              <button
                type="submit"
                disabled={sending || !reply.trim()}
                data-testid="im-send-reply"
                className="wl-btn wl-btn--primary"
                aria-label="Invia risposta"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                <span className="hidden sm:inline">Invia</span>
              </button>
            </div>
            <AttachmentPicker
              files={replyFiles}
              setFiles={setReplyFiles}
              disabled={sending}
              testIdPrefix="im-reply-att"
            />
          </form>
        )}
      </div>
      <UserCardDialog userId={cardUserId} onClose={() => setCardUserId(null)} />
    </div>
  );
};


// ───────────────────────────────────────────────────────────────────
// Lista ticket (riga compatta)
// ───────────────────────────────────────────────────────────────────

const TicketRow = ({ t, onOpen, view, searchQuery }) => {
  // view: 'inbox' or 'sent'
  const rNames = (t.recipient_user_names && t.recipient_user_names.length)
    ? t.recipient_user_names
    : (t.recipient_user_name ? [t.recipient_user_name] : []);
  const recipLabel = rNames.length === 0
    ? "—"
    : rNames.length === 1
      ? rNames[0]
      : `${rNames[0]} +${rNames.length - 1}`;
  const isGroup = rNames.length > 1;
  const recipFull = rNames.join(", ");
  const personLabel = view === "inbox"
    ? <><ArrowDownLeft size={11} /> da <b>{t.opener_user_name}</b></>
    : <span title={recipFull}><ArrowUpRight size={11} className="inline" /> a <b>{recipLabel}</b></span>;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(t)}
        data-testid={`im-row-${t.id}`}
        className="w-full text-left py-3 px-1 hover:bg-subtle/50 transition-colors"
      >
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <CategoryBadge category={t.category} />
          <StatusBadge status={t.status} />
          <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">{personLabel}</span>
          {isGroup && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent/10 text-accent text-[10px] font-medium border border-accent/30"
              title={recipFull}
              data-testid={`im-row-group-${t.id}`}
            >
              Gruppo · {rNames.length}
            </span>
          )}
        </div>
        <div className={`text-sm truncate flex items-center gap-2 ${t.unread ? "font-semibold text-text-primary" : "font-medium text-text-primary"}`}>
          {t.unread && (
            <span
              className="w-2 h-2 rounded-full bg-accent flex-shrink-0"
              data-testid={`im-unread-dot-${t.id}`}
              title="Non letto"
            />
          )}
          {searchQuery ? <Highlight text={t.subject} query={searchQuery} /> : t.subject}
        </div>
        {searchQuery && (
          <div className="mt-1 flex items-start gap-2 flex-wrap" data-testid={`im-search-match-${t.id}`}>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-subtle text-[10px] font-medium text-text-secondary border border-border flex-shrink-0">
              {t.match_in === "subject" ? "Trovato nel titolo" : "Trovato in un messaggio"}
            </span>
            {t.match_in !== "subject" && t.match_snippet && (
              <span className="text-[11px] text-text-muted min-w-0 truncate">
                {t.match_author ? <b>{t.match_author}: </b> : null}
                <Highlight text={t.match_snippet} query={searchQuery} />
              </span>
            )}
          </div>
        )}
        <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2">
          <MessagesSquare size={11} /> {t.message_count} msg
          <span>·</span>
          <Clock3 size={11} /> {fmtRel(t.last_message_at)}
        </div>
      </button>
    </li>
  );
};

const TicketList = ({ items, loading, onOpen, view, emptyMessage, failed, onRetry }) => {
  if (loading) {
    return <div className="text-sm text-text-muted py-6 text-center"><Loader2 size={18} className="animate-spin mx-auto mb-1" />Caricamento…</div>;
  }
  if (failed) {
    return <LoadErrorBox what="i messaggi" onRetry={onRetry} testId="im-list-error" />;
  }
  if (!items.length) {
    return (
      <div className="text-sm text-text-muted py-10 text-center">
        <Inbox size={28} className="mx-auto mb-2 opacity-50" />
        {emptyMessage}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border" data-testid={`im-list-${view}`}>
      {items.map((t) => <TicketRow key={t.id} t={t} onOpen={onOpen} view={view} />)}
    </ul>
  );
};


// ───────────────────────────────────────────────────────────────────
// Pagina principale
// ───────────────────────────────────────────────────────────────────

export default function InternalMessages() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingSent, setLoadingSent] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Apertura thread = letto lato server: rifetcha le liste per aggiornare dot/bold
  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("wl-unread-refresh", bump);
    return () => window.removeEventListener("wl-unread-refresh", bump);
  }, []);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 300);
  const isSearchActive = debouncedQuery.trim().length >= 2;

  useEffect(() => {
    if (isSuperAdmin) return;
    const q = debouncedQuery.trim();
    if (q.length < 2) { setSearchResults(null); return; }
    let cancelled = false;
    setSearching(true);
    api.get("/internal/search", { params: { q } })
      .then(({ data }) => { if (!cancelled) setSearchResults(data.results || []); })
      .catch(() => { if (!cancelled) setSearchResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, isSuperAdmin, refreshKey]);

  const [listFailed, setListFailed] = useState(false);
  const reload = useCallback(async () => {
    setLoadingInbox(true);
    setLoadingSent(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/internal/tickets/inbox"),
        api.get("/internal/tickets/sent"),
      ]);
      setInbox(a.data || []);
      setSent(b.data || []);
      setListFailed(false);
    } catch {
      setListFailed(true);
    } finally {
      setLoadingInbox(false);
      setLoadingSent(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) return;
    reload();
  }, [reload, refreshKey, isSuperAdmin]);

  const inboxUnread = useMemo(
    () => inbox.filter((t) => t.unread).length,
    [inbox]
  );
  const sentUnread = useMemo(
    () => sent.filter((t) => t.unread).length,
    [sent]
  );

  if (isSuperAdmin) {
    return (
      <div className="px-6 py-10 max-w-xl mx-auto text-center" data-testid="im-superadmin-block">
        <MessageSquare size={28} className="mx-auto text-text-muted mb-3" />
        <h2 className="text-base font-semibold text-text-primary mb-2">Area privata del cliente</h2>
        <p className="text-sm text-text-muted">
          I messaggi interni sono comunicazioni private delle aziende clienti.
          Come Wattline non hai accesso a questi thread: usa il modulo Supporto per le interazioni con i clienti.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6" data-testid="im-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <MessageSquare size={22} className="text-accent" /> Messaggi interni
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Scrivi a un collega della tua azienda. I messaggi restano privati: nessuno fuori dall'azienda può leggerli.
          </p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          data-testid="open-new-internal-btn"
          className="bg-accent hover:bg-accent/90 text-white"
        >
          <Plus size={14} className="mr-1" /> Nuovo messaggio
        </Button>
      </div>

      {/* Barra di ricerca */}
      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Cerca in oggetto e messaggi…"
        testId="im-search"
      />

      {isSearchActive ? (
        <Section title={`Risultati per «${debouncedQuery.trim()}»`} icon={Inbox}>
          {searching && searchResults === null ? (
            <div className="text-sm text-text-muted py-6 text-center">
              <Loader2 size={18} className="animate-spin mx-auto mb-1" />Ricerca…
            </div>
          ) : !searchResults || searchResults.length === 0 ? (
            <div className="text-sm text-text-muted py-10 text-center" data-testid="im-search-empty">
              <Inbox size={28} className="mx-auto mb-2 opacity-50" />
              Nessun messaggio contiene «{debouncedQuery.trim()}»
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="im-search-results">
              {searchResults.map((t) => (
                <TicketRow
                  key={t.id}
                  t={t}
                  onOpen={setOpenTicket}
                  view={t.direction === "sent" ? "sent" : "inbox"}
                  searchQuery={debouncedQuery.trim()}
                />
              ))}
            </ul>
          )}
        </Section>
      ) : (
        <>
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        <button
          onClick={() => setTab("inbox")}
          data-testid="im-tab-inbox"
          className={`tab-neon relative px-4 py-2 text-sm font-medium flex items-center gap-2 ${
            tab === "inbox" ? "is-active text-text-primary" : "text-text-muted"
          }`}
        >
          <ArrowDownLeft size={14} /> Ricevuti
          {inboxUnread > 0 && (
            <span data-testid="im-tab-inbox-unread" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">
              {inboxUnread > 99 ? "99+" : inboxUnread}
            </span>
          )}
          {tab === "inbox" && <span className="tab-neon-underline" />}
        </button>
        <button
          onClick={() => setTab("sent")}
          data-testid="im-tab-sent"
          className={`tab-neon relative px-4 py-2 text-sm font-medium flex items-center gap-2 ${
            tab === "sent" ? "is-active text-text-primary" : "text-text-muted"
          }`}
        >
          <ArrowUpRight size={14} /> Inviati
          {sentUnread > 0 && (
            <span data-testid="im-tab-sent-unread" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">
              {sentUnread > 99 ? "99+" : sentUnread}
            </span>
          )}
          {tab === "sent" && <span className="tab-neon-underline" />}
        </button>
      </div>

      <Section title={tab === "inbox" ? "Ricevuti" : "Inviati"} icon={tab === "inbox" ? Inbox : Send}>
        {tab === "inbox" ? (
          <TicketList
            items={inbox}
            loading={loadingInbox}
            onOpen={setOpenTicket}
            view="inbox"
            emptyMessage="Nessun messaggio in arrivo dai colleghi."
            failed={listFailed}
            onRetry={reload}
          />
        ) : (
          <TicketList
            items={sent}
            loading={loadingSent}
            onOpen={setOpenTicket}
            view="sent"
            emptyMessage="Non hai ancora inviato messaggi."
            failed={listFailed}
            onRetry={reload}
          />
        )}
      </Section>
        </>
      )}

      <NewInternalTicketModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(t) => { setRefreshKey((k) => k + 1); setTab("sent"); setOpenTicket(t); }}
      />

      {openTicket && (
        <InternalThread
          ticket={openTicket}
          onClose={() => setOpenTicket(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          currentUser={user}
        />
      )}
    </div>
  );
}
